import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Mail } from 'lucide-react'
import { AuthShell } from '@/components/portal/AuthShell'
import { OtpInput } from '@/components/portal/PasswordInput'
import { fetchAuthSession } from 'aws-amplify/auth'
import { clientApi } from '@/lib/api-client'
import { confirmEmailSignUp, loginWithCognito } from '@/lib/auth'
import { ensureClientProfile } from '@/lib/client-id'
import { clearSignupSessionSecrets, getSignupPassword } from '@/lib/signup-session'
import { useAuthStore } from '@/store/auth'

const VERIFY_EMAIL_KEY = 'esafx_verify_email'

function resolveVerifyEmail(stateEmail: string | undefined, authEmail: string | undefined): string {
  return (
    stateEmail?.trim() ||
    authEmail?.trim() ||
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(VERIFY_EMAIL_KEY) : null) ||
    ''
  )
}

async function hasAuthTokens(): Promise<boolean> {
  try {
    const session = await fetchAuthSession()
    return Boolean(session.tokens?.accessToken)
  } catch {
    return false
  }
}

function isAlreadyAuthenticated(err: unknown): boolean {
  return Boolean(err && typeof err === 'object' && 'name' in err && err.name === 'UserAlreadyAuthenticatedException')
}

/** Logs the just-verified user in and sends them to the dashboard (what the removed
 * /verify-phone no-op step used to do on "success" — there is no phone-OTP vendor). */
async function completeSignupLogin(email: string, navigate: (path: string, opts?: { replace?: boolean; state?: unknown }) => void) {
  if (await hasAuthTokens()) {
    navigate('/dashboard', { replace: true })
    return
  }
  const password = getSignupPassword()
  if (!password) {
    navigate('/login', { replace: true, state: { notice: 'verifiedSignIn' } })
    return
  }
  try {
    await loginWithCognito({ email, password, rememberMe: true })
    try {
      await ensureClientProfile(useAuthStore.getState().user)
    } catch {
      // optional bootstrap
    }
    clearSignupSessionSecrets()
    navigate('/dashboard', { replace: true })
  } catch (err) {
    if (isAlreadyAuthenticated(err) || (await hasAuthTokens())) {
      clearSignupSessionSecrets()
      navigate('/dashboard', { replace: true })
      return
    }
    clearSignupSessionSecrets()
    navigate('/login', { replace: true, state: { notice: 'verifiedSignIn' } })
  }
}

export function VerifyEmailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const authEmail = useAuthStore((s) => s.user?.email)
  const state = (location.state as { email?: string } | null) ?? {}
  const email = resolveVerifyEmail(state.email, authEmail)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email) {
      setError(t('validation.invalidEmail'))
      return
    }
    if (code.length !== 6) {
      setError(t('validation.required'))
      return
    }
    setError(null)
    setIsSubmitting(true)
    try {
      if (import.meta.env.VITE_SIMULATE_EXTERNAL === 'true') {
        await clientApi.confirmEmailOtp(email, code)
      } else {
        await confirmEmailSignUp(email, code)
      }
      await completeSignupLogin(email, navigate)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <h1 className="prod-auth__title">{t('auth.verifyThanks')}</h1>
      <p className="prod-auth__subtitle">{t('auth.verifyEmailIntro')}</p>
      <p className="prod-auth__subtitle prod-auth__subtitle--step">
        {t('auth.verifyStep1')}
      </p>
      <p className="prod-otp-dest">
        <Mail className="prod-otp-dest__icon size-4" aria-hidden="true" />
        <span>
          {email ? (
            <>
              {t('auth.verifyEmailSent')} <strong className="prod-text-strong">{email}</strong>
            </>
          ) : (
            t('auth.codeSentEmail')
          )}
        </span>
      </p>
      <form onSubmit={(e) => void onSubmit(e)}>
        <OtpInput value={code} onChange={setCode} />
        {error ? <p className="prod-error prod-error--center">{error}</p> : null}
        <button type="submit" className="prod-btn prod-btn--gold prod-btn--gold-full" disabled={isSubmitting}>
          {t('auth.verify')}
        </button>
        <p className="prod-auth__subtitle prod-auth__subtitle--after">
          <button type="button" className="prod-link prod-link-btn">
            {t('auth.resendCode')}
          </button>
        </p>
        <p className="prod-auth__subtitle prod-auth__subtitle--caption">
          {t('auth.supportHint')}{' '}
          <a href="mailto:support@esafx.co.id" className="prod-link">
            support@esafx.co.id
          </a>
        </p>
      </form>
    </AuthShell>
  )
}
